/**
 * Minimal toast system for viisi-web.
 *
 * One global $state queue lives in this module (.svelte.ts so runes work).
 * <Toast /> is mounted once in AppShell. Anything in the app can call:
 *
 *   toast.show('Paino tallennettu')
 *
 * Toasts are the proto's single ink pill above the bottom nav: short, one
 * at a time, auto-dismissed. `success` and `info` are aliases kept for the
 * copied components.
 */

export interface ToastItem {
    id: number;
    message: string;
}

const MAX_TOASTS = 2;
const DISMISS_AFTER_MS = 1800;

let nextId = 1;

const store = $state<{ items: ToastItem[] }>({ items: [] });

const timers = new Map<number, ReturnType<typeof setTimeout>>();

function push(message: string): void {
    const id = nextId++;
    const next: ToastItem[] = [...store.items, { id, message }];
    while (next.length > MAX_TOASTS) {
        const dropped = next.shift();
        if (dropped) {
            const t = timers.get(dropped.id);
            if (t) { clearTimeout(t); timers.delete(dropped.id); }
        }
    }
    store.items = next;
    const handle = setTimeout(() => dismiss(id), DISMISS_AFTER_MS);
    timers.set(id, handle);
}

export function dismiss(id: number): void {
    const t = timers.get(id);
    if (t) { clearTimeout(t); timers.delete(id); }
    store.items = store.items.filter((item) => item.id !== id);
}

export const toast = {
    /** Reactive list of currently visible toasts. */
    get items(): ToastItem[] { return store.items; },
    show(message: string): void { push(message); },
    success(message: string): void { push(message); },
    info(message: string): void { push(message); },
    dismiss,
};
